import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const InsuranceVerification = ({ data, onUpdate }) => {
  const [formData, setFormData] = useState({
    hasInsurance: data?.insurance?.hasInsurance !== undefined ? data?.insurance?.hasInsurance : true,
    insuranceProvider: data?.insurance?.insuranceProvider || '',
    policyNumber: data?.insurance?.policyNumber || '',
    groupNumber: data?.insurance?.groupNumber || '',
    subscriberName: data?.insurance?.subscriberName || '',
    subscriberDOB: data?.insurance?.subscriberDOB || '',
    relationshipToSubscriber: data?.insurance?.relationshipToSubscriber || '',
    ...data?.insurance
  });

  const [verificationStatus, setVerificationStatus] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const insuranceProviders = [
    { value: 'delta-dental', label: 'Delta Dental' },
    { value: 'cigna', label: 'Cigna Dental' },
    { value: 'metlife', label: 'MetLife' },
    { value: 'aetna', label: 'Aetna' },
    { value: 'guardian', label: 'Guardian' },
    { value: 'humana', label: 'Humana' },
    { value: 'united', label: 'United Healthcare' },
    { value: 'bcbs', label: 'Blue Cross Blue Shield' },
    { value: 'other', label: 'Other' }
  ];

  const relationships = [
    { value: 'self', label: 'Self' },
    { value: 'spouse', label: 'Spouse' },
    { value: 'child', label: 'Child' },
    { value: 'other', label: 'Other Dependent' }
  ];

  const handleChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    onUpdate('insurance', updated);
    if (verificationStatus) {
      setVerificationStatus(null);
    }
  };

  const handleVerifyInsurance = async () => {
    setVerifying(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const mockVerification = {
        status: 'active',
        coverageDetails: {
          preventive: '100%',
          basic: '80%',
          major: '50%',
          annualMaximum: '$1,500',
          deductible: '$50',
          deductibleMet: '$25'
        }
      };
      
      setVerificationStatus(mockVerification);
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationStatus({ status: 'error', message: 'Unable to verify insurance' });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-2 flex items-center gap-2">
          <Icon name="Shield" size={24} className="text-primary" />
          Insurance Information
        </h2>
        <p className="text-sm text-muted-foreground">
          Provide your dental insurance details for benefits verification.
        </p>
      </div>

      {/* Has Insurance Toggle */}
      <div className="flex items-center gap-3 p-4 bg-background rounded-lg border border-border">
        <input
          type="checkbox"
          checked={formData?.hasInsurance}
          onChange={(e) => handleChange('hasInsurance', e?.target?.checked)}
          className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary"
        />
        <label className="text-sm font-medium text-foreground">
          I have dental insurance
        </label>
      </div>

      {formData?.hasInsurance ? (
        <>
          {/* Insurance Provider */}
          <Select
            label="Insurance Provider"
            options={insuranceProviders}
            value={formData?.insuranceProvider}
            onChange={(value) => handleChange('insuranceProvider', value)}
            placeholder="Select your insurance provider"
            required
          />

          {/* Policy Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Policy Number"
              type="text"
              value={formData?.policyNumber}
              onChange={(e) => handleChange('policyNumber', e?.target?.value)}
              placeholder="Enter policy number"
              required
            />
            <Input
              label="Group Number"
              type="text"
              value={formData?.groupNumber}
              onChange={(e) => handleChange('groupNumber', e?.target?.value)}
              placeholder="Enter group number (if applicable)"
            />
          </div>

          {/* Subscriber Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground">Subscriber Information</h3>
            
            <Select
              label="Relationship to Subscriber"
              options={relationships}
              value={formData?.relationshipToSubscriber}
              onChange={(value) => handleChange('relationshipToSubscriber', value)}
              placeholder="Select relationship"
              required
            />

            {formData?.relationshipToSubscriber !== 'self' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Subscriber Name"
                  type="text"
                  value={formData?.subscriberName}
                  onChange={(e) => handleChange('subscriberName', e?.target?.value)}
                  placeholder="Full name of policy holder"
                  required
                />
                <Input
                  label="Subscriber Date of Birth"
                  type="date"
                  value={formData?.subscriberDOB}
                  onChange={(e) => handleChange('subscriberDOB', e?.target?.value)}
                  required
                />
              </div>
            )}
          </div>

          {/* Verify Insurance Button */}
          <div className="flex items-center gap-3">
            <Button
              variant="default"
              iconName="Search"
              iconPosition="left"
              onClick={handleVerifyInsurance}
              loading={verifying}
              disabled={!formData?.insuranceProvider || !formData?.policyNumber}
            >
              {verifying ? 'Verifying...' : 'Verify Insurance'}
            </Button>
            {verificationStatus && (
              <span className={`text-sm font-medium ${
                verificationStatus?.status === 'active' ? 'text-success' : 'text-destructive'
              }`}>
                {verificationStatus?.status === 'active' ? '✓ Verified' : '✗ Verification Failed'}
              </span>
            )}
          </div>

          {/* Verification Results */}
          {verificationStatus?.status === 'active' && (
            <div className="bg-success/5 rounded-lg border border-success/20 p-4">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-3">Insurance Verified</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Preventive Care</p>
                      <p className="font-semibold text-foreground">{verificationStatus?.coverageDetails?.preventive}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Basic Procedures</p>
                      <p className="font-semibold text-foreground">{verificationStatus?.coverageDetails?.basic}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Major Procedures</p>
                      <p className="font-semibold text-foreground">{verificationStatus?.coverageDetails?.major}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Annual Maximum</p>
                      <p className="font-semibold text-foreground">{verificationStatus?.coverageDetails?.annualMaximum}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Deductible</p>
                      <p className="font-semibold text-foreground">{verificationStatus?.coverageDetails?.deductible}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Deductible Met</p>
                      <p className="font-semibold text-foreground">{verificationStatus?.coverageDetails?.deductibleMet}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-muted/50 rounded-lg border border-border p-6 text-center">
          <Icon name="Info" size={32} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            No problem! We offer flexible payment options for patients without insurance.
            Our team will discuss payment plans during your visit.
          </p>
        </div>
      )}
    </div>
  );
};

export default InsuranceVerification;